<template>
  <default-modal
    v-model="opened"
    name="invoice-payment-modal"
    :actionLabel="$t('send')"
    :autofocus="$refs.amountField"
    :successHandler="handleSend"
    :title="$t('payment_modal_title')"
    width="sm"
  >
    <div slot="content">
      <q-field>
        <q-checkbox v-model="formData.markAsPaid" :label="$t('add_full_payment')" name="markInvoiceAsPaid" />
      </q-field>

      <div class="row gutter-md">
        <div class="col-md">
          <q-field :error="errors.has('amount')" :error-label="errors.first('amount')">
            <q-input
              v-model="formData.payment"
              v-validate.disable="'required|max:45'"
              :data-vv-as="$tc('amount', 1)"
              :float-label="$tc('amount', 1)"
              :disable="formData.markAsPaid"
              name="amount"
              ref="amountField"
            />
          </q-field>
        </div>

        <div class="col-md">
           <q-field>
            <q-select
              v-model="formData.paymentType"
              :float-label="$t('payment_type')"
              :options="paymentTypeOptions"
              name="paymentType"
              clearable
              radio
            />
          </q-field>
        </div>

        <div class="col-md">
           <q-field>
            <q-datetime
              v-model="formData.paymentDate"
              :float-label="$t('payment_date')"
              :format="$t('date_format')"
              name="paymentDate"
              type="date"
              clearable
            />
          </q-field>
        </div>
      </div>
      <div v-if="showDifferenceReason" class="row gutter-md">
        <div class="col">
          <q-field :error="errors.has('difference_reason')" :error-label="errors.first('difference_reason')">
            <q-select
              v-model="formData.differenceReason"
              :float-label="$t('difference_reason')"
              :options="differenceReasons"
              :data-vv-as="$t('difference_reason')"
              name="difference_reason"
              v-validate.disable="'required'"
            />
          </q-field>
        </div>
      </div>
    </div>
  </default-modal>
</template>

<script>
  import Decimal from 'decimal.js'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { InvoiceClient } from 'src/api/Invoices'
  import { date } from 'quasar'
  import { formatMoney } from 'src/utils/format'
  import { invoices } from 'src/config'

  function replaceDecimalSign (value) {
    if (typeof value === 'string') {
      value = value.replace(/,/, '.')
    }

    return value
  }

  function priceInCent (price) {
    price = replaceDecimalSign(price)
    let value = 0

    if (!isNaN(price) && price !== null && price !== '') {
      value = new Decimal(price).times(100).round().toFixed()
    }

    return value
  }

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        invoice: null,
        opened: false,
        formData: {
          markAsPaid: false,
          payment: null,
          paymentType: null,
          paymentDate: new Date(),
          differenceReason: null
        }
      }
    },

    computed: {
      handleSend () {
        return this.formData.markAsPaid ? this.setPaid : this.addPayment
      },

      paymentTypeOptions () {
        return invoices.payment_methods.map(method => ({ label: this.$t(method), value: method }))
      },

      showDifferenceReason () {
        if (this.invoice === null || this.formData.markAsPaid === true || this.formData.payment === null) {
          return false
        }

        return (this.invoice.total_gross - this.invoice.paid_balance) !== parseInt(priceInCent(this.formData.payment))
      },

      differenceReasons () {
        return [
          {
            value: 'partial',
            label: this.$t('difference_reason_partial')
          },
          {
            value: 'cash_discount',
            label: this.$t('difference_reason_cash_discount')
          },
          {
            value: 'currency_conversion',
            label: this.$t('difference_reason_currency_conversion')
          }
        ]
      }
    },

    props: {
      includes: {
        required: true,
        type: Array
      }
    },

    methods: {
      open (invoice = null) {
        this.invoice = invoice
        this.opened = true
      },

      reset () {
        this.invoice = null
        this.opened = false
        this.formData = {
          markAsPaid: false,
          payment: null,
          paymentType: null,
          paymentDate: new Date()
        }
      },

      setPaid () {
        const paymentDate = date.formatDate(this.formData.paymentDate, 'YYYY-MM-DD')

        return new InvoiceClient()
          .include(this.includes)
          .addPayment(this.invoice.id, { payment_method: this.formData.paymentType, payment_date: paymentDate })
          .then(response => {
            this.$q.notify({ message: this.$t('payment_set_as_paid'), type: 'positive' })
            this.$emit('updatedInvoice', response.data.data)
            this.reset()
          })
      },

      addPayment () {
        const paymentDate = date.formatDate(this.formData.paymentDate, 'YYYY-MM-DD')

        return this.$validator.validateAll()
          .then(result => {
            if (result) {
              const payment = priceInCent(this.formData.payment)

              return new InvoiceClient()
                .include(this.includes)
                .addPayment(this.invoice.id, {
                  balance: payment,
                  payment_method: this.formData.paymentType,
                  payment_date: paymentDate,
                  difference_reason: this.formData.differenceReason
                })
                .then(response => {
                  this.$emit('updatedInvoice', response.data.data)
                  this.$q.notify({ message: this.$t('payment_modal_added_payment', { value: formatMoney(payment, this.invoice.currency) }), type: 'positive' })
                  this.reset()
                })
            }
          })
      }
    },

    watch: {
      opened (val) {
        if (!val) {
          this.reset()
        }
      }
    },

    components: {
      DefaultModal
    }
  }
</script>
